import { Injectable } from '@angular/core';

export const STAMP_DUTY_CONSTRAINTS = {
  saleValue:     { min: 1_00_000, max: 20_00_00_000, step: 10_000 },
  guidanceValue: { min: 0,        max: 20_00_00_000, step: 10_000 }
} as const;

export type KarnatakaAreaType = 'urban' | 'rural';

/** Cess is a flat 10% of stamp duty, statewide (Karnataka Stamp Act). */
const CESS_RATE = 0.10;

/**
 * Surcharge on stamp duty: 2% inside BBMP / City Corporation / City Municipal Council limits,
 * 3% inside Gram Panchayat (BMRDA) limits.
 */
const SURCHARGE_RATE: Record<KarnatakaAreaType, number> = { urban: 0.02, rural: 0.03 };

/** Flat registration fee, statewide, since the 31 Aug 2025 revision (doubled from the earlier 1%). */
const REGISTRATION_RATE = 0.02;

export interface StampDutySlab {
  max: number | null;
  rate: number;
  label: string;
}

/** Karnataka stamp duty is a flat slab rate on the whole value, not a marginal/tiered rate. */
export const STAMP_DUTY_SLABS: StampDutySlab[] = [
  { max: 20_00_000, rate: 0.02, label: 'Up to ₹20,00,000' },
  { max: 45_00_000, rate: 0.03, label: '₹20,00,001 – ₹45,00,000' },
  { max: null,       rate: 0.05, label: 'Above ₹45,00,000' }
];

export interface StampDutyResult {
  dutyValue: number;
  usedGuidanceValue: boolean;
  slab: StampDutySlab;
  baseStampDuty: number;
  cessRate: number;
  cessAmount: number;
  surchargeRate: number;
  surchargeAmount: number;
  totalStampDuty: number;
  registrationRate: number;
  registrationFee: number;
  totalPayable: number;
  effectiveRate: number;
}

@Injectable({ providedIn: 'root' })
export class StampDutyKarnatakaCalculatorService {

  private slabFor(value: number): StampDutySlab {
    return STAMP_DUTY_SLABS.find(s => value <= (s.max ?? Infinity)) ?? STAMP_DUTY_SLABS[STAMP_DUTY_SLABS.length - 1];
  }

  /**
   * Stamp duty and registration fee are both charged on the higher of the sale consideration and
   * the government guidance value, to prevent under-valuation — the same rule the sub-registrar's
   * office applies at the Kaveri portal.
   */
  calculate(saleConsideration: number, guidanceValue: number, areaType: KarnatakaAreaType): StampDutyResult {
    const dutyValue = Math.max(saleConsideration, guidanceValue || 0);
    const usedGuidanceValue = guidanceValue > saleConsideration;
    const slab = this.slabFor(dutyValue);

    const baseStampDuty = dutyValue * slab.rate;
    const cessAmount = baseStampDuty * CESS_RATE;
    const surchargeRate = SURCHARGE_RATE[areaType];
    const surchargeAmount = baseStampDuty * surchargeRate;
    const totalStampDuty = baseStampDuty + cessAmount + surchargeAmount;

    const registrationFee = dutyValue * REGISTRATION_RATE;
    const totalPayable = totalStampDuty + registrationFee;

    return {
      dutyValue, usedGuidanceValue, slab,
      baseStampDuty, cessRate: CESS_RATE, cessAmount,
      surchargeRate, surchargeAmount, totalStampDuty,
      registrationRate: REGISTRATION_RATE, registrationFee,
      totalPayable, effectiveRate: dutyValue > 0 ? totalPayable / dutyValue : 0
    };
  }
}
